import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import FinancialAccountService from "../../services/FinancialAccountService";

const accountStyles = {
  1: {
    icon: "pi pi-money-bill",
    label: "Kasa",
    badge: "!bg-emerald-50 !text-emerald-700 !border !border-emerald-200 dark:!bg-emerald-950/30 dark:!text-emerald-400 dark:!border-emerald-900/50",
    iconColor: "!text-emerald-600 !bg-emerald-50 !border !border-emerald-200 dark:!text-emerald-400 dark:!bg-emerald-950/30 dark:!border-emerald-900/50",
    accent: "!bg-emerald-500",
    leftBorder: "!border-l-emerald-500",
    cardBg: "bg-gradient-to-br from-white via-emerald-50/40 to-teal-50/30 dark:from-slate-900 dark:via-emerald-950/10 dark:to-slate-950",
    glow: "hover:shadow-emerald-500/10",
    progress: "bg-gradient-to-r from-emerald-500 to-teal-500",
  },
  2: {
    icon: "pi pi-building",
    label: "Banka",
    badge: "!bg-blue-50 !text-blue-700 !border !border-blue-200 dark:!bg-blue-950/30 dark:!text-blue-400 dark:!border-blue-900/50",
    iconColor: "!text-blue-600 !bg-blue-50 !border !border-blue-200 dark:!text-blue-400 dark:!bg-blue-950/30 dark:!border-blue-900/50",
    accent: "!bg-blue-500",
    leftBorder: "!border-l-blue-500",
    cardBg: "bg-gradient-to-br from-white via-blue-50/40 to-indigo-50/30 dark:from-slate-900 dark:via-blue-950/10 dark:to-slate-950",
    glow: "hover:shadow-blue-500/10",
    progress: "bg-gradient-to-r from-blue-500 to-indigo-500",
  },
  3: {
    icon: "pi pi-credit-card",
    label: "Firma Kartı",
    badge: "!bg-orange-50 !text-orange-700 !border !border-orange-200 dark:!bg-orange-950/30 dark:!text-orange-400 dark:!border-orange-900/50",
    iconColor: "!text-orange-600 !bg-orange-50 !border !border-orange-200 dark:!text-orange-400 dark:!bg-orange-950/30 dark:!border-orange-900/50",
    accent: "!bg-orange-500",
    leftBorder: "!border-l-orange-500",
    cardBg: "bg-gradient-to-br from-white via-orange-50/40 to-amber-50/30 dark:from-slate-900 dark:via-orange-950/10 dark:to-slate-950",
    glow: "hover:shadow-orange-500/10",
    progress: "bg-gradient-to-r from-amber-500 to-orange-500",
  },
  4: {
    icon: "pi pi-print",
    label: "POS Cihazı",
    badge: "!bg-cyan-50 !text-cyan-700 !border !border-cyan-200 dark:!bg-cyan-950/30 dark:!text-cyan-400 dark:!border-cyan-900/50",
    iconColor: "!text-cyan-600 !bg-cyan-50 !border !border-cyan-200 dark:!text-cyan-400 dark:!bg-cyan-950/30 dark:!border-cyan-900/50",
    accent: "!bg-cyan-500",
    leftBorder: "!border-l-cyan-500",
    cardBg: "bg-gradient-to-br from-white via-cyan-50/40 to-sky-50/30 dark:from-slate-900 dark:via-cyan-950/10 dark:to-slate-950",
    glow: "hover:shadow-cyan-500/10",
    progress: "!bg-gradient-to-r !from-cyan-500 !to-sky-500",
  },
  5: {
    icon: "pi pi-globe",
    label: "Sanal POS",
    badge: "!bg-purple-50 !text-purple-700 !border !border-purple-200 dark:!bg-purple-950/30 dark:!text-purple-400 dark:!border-purple-900/50",
    iconColor: "!text-purple-600 !bg-purple-50 !border !border-purple-200 dark:!text-purple-400 dark:!bg-purple-950/30 dark:!border-purple-900/50",
    accent: "!bg-purple-500",
    leftBorder: "!border-l-purple-500",
    cardBg: "bg-gradient-to-br from-white via-purple-50/40 to-fuchsia-50/30 dark:from-slate-900 dark:via-purple-950/10 dark:to-slate-950",
    glow: "hover:shadow-purple-500/10",
    progress: "bg-gradient-to-r from-purple-500 to-fuchsia-500",
  },
  default: {
    icon: "pi pi-wallet",
    label: "Diğer",
    badge: "!bg-slate-50 !text-slate-700 !border !border-slate-200 dark:!bg-slate-800/40 dark:!text-slate-400 dark:!border-slate-700/50",
    iconColor: "!text-slate-600 !bg-slate-50 !border !border-slate-200 dark:!text-slate-400 dark:!bg-slate-800/40 dark:!border-slate-700/50",
    accent: "!bg-slate-500",
    leftBorder: "!border-l-slate-500",
    cardBg: "bg-gradient-to-br from-white via-slate-50/70 to-slate-100/60 dark:from-slate-900 dark:via-slate-800/30 dark:to-slate-950",
    glow: "hover:shadow-slate-500/10",
    progress: "bg-gradient-to-r from-slate-500 to-slate-700",
  },
};

export default function FinancialAccountsListPage() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState(null); // null means "All"
  const [virtualPosDialogVisible, setVirtualPosDialogVisible] = useState(false);
  const [viewMode, setViewMode] = useState(localStorage.getItem("accounts_view_mode") || "grid");
  const [sortBy, setSortBy] = useState("name-asc");

  const changeViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem("accounts_view_mode", mode);
  };

  const filterTypes = [
    {
      label: "Tümü",
      value: null,
      icon: "pi pi-th-large",
      activeClass: "bg-gradient-to-r from-slate-800 to-slate-950 text-white shadow-[0_8px_20px_-6px_rgba(15,23,42,0.4)]",
      hoverClass: "hover:border-slate-300 hover:text-slate-800 hover:bg-slate-50 hover:shadow-md",
      iconBg: "bg-slate-100 text-slate-500 group-hover:bg-slate-200 group-hover:text-slate-700"
    },
    {
      label: "Kasa",
      value: 1,
      icon: "pi pi-money-bill",
      activeClass: "bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-[0_8px_20px_-6px_rgba(16,185,129,0.4)]",
      hoverClass: "hover:border-emerald-300 hover:text-emerald-700 hover:bg-emerald-50/25 hover:shadow-md",
      iconBg: "bg-emerald-50 text-emerald-600 group-hover:bg-emerald-100 group-hover:text-emerald-700"
    },
    {
      label: "Banka",
      value: 2,
      icon: "pi pi-building",
      activeClass: "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-[0_8px_20px_-6px_rgba(59,130,246,0.4)]",
      hoverClass: "hover:border-blue-300 hover:text-blue-700 hover:bg-blue-50/25 hover:shadow-md",
      iconBg: "bg-blue-50 text-blue-600 group-hover:bg-blue-100 group-hover:text-blue-700"
    },
    {
      label: "Firma Kartı",
      value: 3,
      icon: "pi pi-credit-card",
      activeClass: "bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-[0_8px_20px_-6px_rgba(245,158,11,0.4)]",
      hoverClass: "hover:border-orange-300 hover:text-orange-700 hover:bg-orange-50/25 hover:shadow-md",
      iconBg: "bg-orange-50 text-orange-600 group-hover:bg-orange-100 group-hover:text-orange-700"
    },
    {
      label: "POS Cihazı",
      value: 4,
      icon: "pi pi-print",
      activeClass: "bg-gradient-to-r from-cyan-500 to-sky-600 text-white shadow-[0_8px_20px_-6px_rgba(6,182,212,0.4)]",
      hoverClass: "hover:border-cyan-300 hover:text-cyan-700 hover:bg-cyan-50/25 hover:shadow-md",
      iconBg: "bg-cyan-50 text-cyan-600 group-hover:bg-cyan-100 group-hover:text-cyan-700"
    },
    {
      label: "Sanal POS",
      value: 5,
      icon: "pi pi-globe",
      activeClass: "bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-[0_8px_20px_-6px_rgba(168,85,247,0.4)]",
      hoverClass: "hover:border-purple-300 hover:text-purple-700 hover:bg-purple-50/25 hover:shadow-md",
      iconBg: "bg-purple-50 text-purple-600 group-hover:bg-purple-100 group-hover:text-purple-700"
    }
  ];

  const getCount = (typeVal) => {
    if (typeVal === null) {
      const posCount = accounts.some(a => a.accountType === 5) ? 1 : 0;
      return accounts.filter(a => a.accountType !== 5).length + posCount;
    }
    if (typeVal === 5) {
      return accounts.some(a => a.accountType === 5) ? 1 : 0;
    }
    return accounts.filter(a => a.accountType === typeVal).length;
  };

  const navigate = useNavigate();
  const toast = useRef(null);

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      setLoading(true);

      const res = await FinancialAccountService.getAll();

      if (res.success) {
        setAccounts(res.data || []);
      }
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "Hesaplar yüklenirken hata oluştu",
      });
    } finally {
      setLoading(false);
    }
  };

  const [transferDialogVisible, setTransferDialogVisible] = useState(false);
  const [transferData, setTransferData] = useState({
    sourceAccountId: null,
    destinationAccountId: null,
    amount: null,
    description: "",
    transferDate: new Date(),
  });
  const [transferLoading, setTransferLoading] = useState(false);


  const accountOptionTemplate = (option) => {
    if (!option) return null;
    const style = getStyle(option.accountType);
    return (
      <div className="flex items-center justify-between w-full py-1">
        <div className="flex items-center gap-3 min-w-0">
          <i className={`${style.icon} text-slate-500 text-base flex-shrink-0`} />
          <div className="flex flex-col min-w-0">
            <span className="font-bold text-slate-700 text-sm truncate">{option.name}</span>
            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">{style.label}</span>
          </div>
        </div>
        <span className="font-black text-slate-700 text-sm ml-4 flex-shrink-0">
          {(option.balance ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} {option.currencyCode || "TRY"}
        </span>
      </div>
    );
  };

  const openTransferDialog = () => {
    setTransferData({
      sourceAccountId: null,
      destinationAccountId: null,
      amount: null,
      description: "",
      transferDate: new Date(),
    });
    setTransferDialogVisible(true);
  };

  const handleTransfer = async () => {
    const { sourceAccountId, destinationAccountId, amount, description, transferDate } = transferData;

    if (!sourceAccountId) {
      toast.current?.show({ severity: "error", summary: "Hata", detail: "Lütfen kaynak hesabı seçin." });
      return;
    }
    if (!destinationAccountId) {
      toast.current?.show({ severity: "error", summary: "Hata", detail: "Lütfen hedef hesabı seçin." });
      return;
    }
    if (sourceAccountId === destinationAccountId) {
      toast.current?.show({ severity: "error", summary: "Hata", detail: "Kaynak ve hedef hesap aynı olamaz." });
      return;
    }
    if (!amount || amount <= 0) {
      toast.current?.show({ severity: "error", summary: "Hata", detail: "Lütfen geçerli bir tutar girin." });
      return;
    }

    try {
      setTransferLoading(true);
      const res = await FinancialAccountService.transfer({
        sourceAccountId,
        destinationAccountId,
        amount,
        description,
        transferDate: transferDate ? new Date(transferDate).toISOString() : new Date().toISOString(),
      });

      if (res.success) {
        toast.current?.show({ severity: "success", summary: "Başarılı", detail: "Bakiye transferi başarıyla gerçekleşti." });
        setTransferDialogVisible(false);
        loadAccounts();
      } else {
        toast.current?.show({ severity: "error", summary: "Hata", detail: res.message || "Transfer işlemi başarısız." });
      }
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: err.response?.data?.message || "Transfer sırasında bir hata oluştu.",
      });
    } finally {
      setTransferLoading(false);
    }
  };

  const filteredAccounts = useMemo(() => {
    const rawFiltered = selectedType
      ? accounts.filter(a => a.accountType === selectedType)
      : accounts;

    const nonVirtualPos = rawFiltered.filter(a => a.accountType !== 5);
    const virtualPos = rawFiltered.filter(a => a.accountType === 5);

    let result = [];
    if (virtualPos.length > 0) {
      const totalAmount = accounts.filter(a => a.accountType === 5).reduce((sum, a) => sum + (a.totalTransactionAmount ?? 0), 0);
      const totalBalance = accounts.filter(a => a.accountType === 5).reduce((sum, a) => sum + (a.balance ?? 0), 0);

      const groupedCard = {
        id: "grouped-virtual-pos",
        name: "Sanal Pos",
        accountType: 5,
        accountNumber: "TÜMÜ",
        totalTransactionAmount: totalAmount,
        balance: totalBalance,
        currencyCode: "TRY",
        isGrouped: true
      };

      result = [...nonVirtualPos, groupedCard];
    } else {
      result = nonVirtualPos;
    }

    // Apply sorting
    return [...result].sort((x, y) => {
      if (sortBy === "name-asc") {
        return x.name.localeCompare(y.name, "tr");
      }
      if (sortBy === "name-desc") {
        return y.name.localeCompare(x.name, "tr");
      }
      if (sortBy === "balance-desc") {
        const valX = x.accountType === 5 ? (x.totalTransactionAmount ?? 0) : (x.balance ?? 0);
        const valY = y.accountType === 5 ? (y.totalTransactionAmount ?? 0) : (y.balance ?? 0);
        return valY - valX;
      }
      if (sortBy === "balance-asc") {
        const valX = x.accountType === 5 ? (x.totalTransactionAmount ?? 0) : (x.balance ?? 0);
        const valY = y.accountType === 5 ? (y.totalTransactionAmount ?? 0) : (y.balance ?? 0);
        return valX - valY;
      }
      if (sortBy === "type") {
        return x.accountType - y.accountType;
      }
      return 0;
    });
  }, [accounts, selectedType, sortBy]);

  const getTotals = () => {
    let cash = 0;
    let bank = 0;
    let cardDebt = 0;
    let cardLimit = 0;
    let pos = 0;
    let virtualPosTurnover = 0;

    accounts.forEach(a => {
      const balance = a.balance ?? 0;
      if (a.accountType === 1) {
        cash += balance;
      } else if (a.accountType === 2) {
        bank += balance;
      } else if (a.accountType === 3) {
        if (balance < 0) {
          cardDebt += Math.abs(balance);
        } else {
          cardDebt -= balance;
        }
        cardLimit += a.cardLimit ?? 0;
      } else if (a.accountType === 4) {
        pos += balance;
      } else if (a.accountType === 5) {
        virtualPosTurnover += a.totalTransactionAmount ?? 0;
      }
    });

    return { cash, bank, cardDebt, cardLimit, pos, virtualPosTurnover };
  };

  const totals = getTotals();

  const summaryCards = [
    {
      title: "Kasa Toplamı",
      value: totals.cash,
      caption: "Nakit Mevcudu",
      icon: "pi pi-money-bill",
      wrapper: "from-emerald-500 to-teal-600",
      surface: "from-white via-emerald-50/60 to-teal-50/40 dark:from-slate-900 dark:via-emerald-950/15 dark:to-slate-950",
      text: "text-emerald-600 dark:text-emerald-400",
    },
    {
      title: "Banka Mevduatı",
      value: totals.bank,
      caption: "Hesap Mevduatı",
      icon: "pi pi-building",
      wrapper: "from-blue-500 to-indigo-600",
      surface: "from-white via-blue-50/60 to-indigo-50/40 dark:from-slate-900 dark:via-blue-950/15 dark:to-slate-950",
      text: "text-blue-600 dark:text-blue-400",
    },
    {
      title: "Firma Kart Borcu",
      value: totals.cardDebt,
      caption: `Limit: ${totals.cardLimit.toLocaleString("tr-TR")} ₺`,
      icon: "pi pi-credit-card",
      wrapper: "from-rose-500 to-orange-500",
      surface: "from-white via-rose-50/60 to-orange-50/40 dark:from-slate-900 dark:via-rose-950/15 dark:to-slate-950",
      text: "text-rose-600 dark:text-rose-400",
    },
    {
      title: "POS Tahsilatı",
      value: totals.pos,
      caption: "POS Mevcudu",
      icon: "pi pi-print",
      wrapper: "from-cyan-500 to-sky-600",
      surface: "from-white via-cyan-50/60 to-sky-50/40 dark:from-slate-900 dark:via-cyan-950/15 dark:to-slate-950",
      text: "text-cyan-600 dark:text-cyan-400",
    },
    {
      title: "Sanal POS Cirosu",
      value: totals.virtualPosTurnover,
      caption: "Toplam İşlem Tutarı",
      icon: "pi pi-globe",
      wrapper: "from-purple-500 to-fuchsia-600",
      surface: "from-white via-purple-50/60 to-fuchsia-50/40 dark:from-slate-900 dark:via-purple-950/15 dark:to-slate-950",
      text: "text-purple-600 dark:text-purple-400",
    },
  ];

  const getStyle = (type) => accountStyles[type] || accountStyles.default;

  return (
    <div className="relative p-2 md:p-4 space-y-4 animate-fade-in text-slate-700  ">
      <Toast ref={toast} appendTo={mounted ? document.body : "self"} />
      {/* Page Header */}
      <div className="relative overflow-hidden rounded-[2rem] border border-white/70 dark:border-slate-800/80 bg-slate-950 shadow-2xl shadow-slate-900/10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_12%_18%,rgba(34,197,94,0.32),transparent_24%),radial-gradient(circle_at_78%_18%,rgba(59,130,246,0.34),transparent_28%),radial-gradient(circle_at_56%_88%,rgba(168,85,247,0.28),transparent_28%)]" />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/95 via-slate-900/92 to-slate-950/95" />
        <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-5 p-4 md:p-6">
          <div className="flex items-start gap-4 min-w-0">
            <div className="w-14 h-14 rounded-2xl bg-white/10 text-white border border-white/15 shadow-lg flex items-center justify-center shrink-0">
              <i className="pi pi-wallet text-2xl" />
            </div>
            <div className="min-w-0">
              <span className="inline-flex items-center gap-2 text-[10px] font-black tracking-[0.25em] uppercase !text-cyan-200 bg-white/10 border border-white/10 px-3 py-1 rounded-full mb-3">
                <i className="pi pi-sparkles text-[12px]" /> Finans Merkezi
              </span>
              <h1 className="text-2xl md:text-3xl font-black text-white m-0 tracking-tight">
                Hesaplarım
              </h1>
              <p className="text-sm text-slate-300 font-semibold m-0 mt-2 max-w-2xl leading-relaxed">
                Kasa, banka, firma kartı, POS ve Sanal POS hesaplarınızı tek ekrandan takip edin; bakiyeleri, ciroları ve hesap hareketlerini daha net yönetin.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 shrink-0">
            <Button
              label="Bakiye Transferi"
              icon="pi pi-sync"
              className="!bg-white/10 hover:!bg-white/15 !text-white !border !border-white/15 font-black text-xs px-4 py-3 rounded-2xl shadow-sm backdrop-blur"
              onClick={openTransferDialog}
            />
            <Button
              label="Yeni Hesap"
              icon="pi pi-plus"
              className="!bg-white hover:!bg-slate-100 !text-slate-950 !border-none font-black text-xs px-4 py-3 rounded-2xl shadow-lg shadow-white/10"
              onClick={() => navigate("/finance/accounts/add")}
            />
          </div>
        </div>
      </div>
      {/* Financial Summary Widgets */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        {summaryCards.map((card) => (
          <div
            key={card.title}
            className={`relative overflow-hidden rounded-3xl border border-white/80 dark:border-slate-800/80 bg-gradient-to-br ${card.surface} p-4 shadow-lg shadow-slate-200/60 dark:shadow-black/20 hover:-translate-y-1 hover:shadow-xl transition-all duration-300`}
          >
            <div className={`absolute -right-7 -top-7 w-24 h-24 rounded-full bg-gradient-to-br ${card.wrapper} opacity-15`} />
            <div className="relative flex items-start justify-between gap-3">
              <div className="min-w-0">
                <span className="text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.2em] block">
                  {card.title}
                </span>
                <h3 className={`text-lg md:text-xl font-black ${card.text} tracking-tight mt-2 mb-1 truncate`}>
                  {card.value.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
                </h3>
                <span className="inline-flex items-center gap-1 text-[9px] text-slate-500 dark:text-slate-400 font-black bg-white/70 dark:bg-slate-950/40 border border-white/70 dark:border-slate-800/70 px-2 py-1 rounded-lg">
                  <i className="pi pi-chart-line text-[8px]" /> {card.caption}
                </span>
              </div>
              <div className={`w-3 h-12 rounded-2xl bg-gradient-to-br ${card.wrapper} text-white flex items-center justify-center shadow-lg shrink-0`}>
                <i className={`${card.icon} text-base`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Control Bar (Filters & Toggle View) */}
      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 rounded-3xl border border-white/80 dark:border-slate-800/80 bg-white/80 dark:bg-slate-900/70 backdrop-blur p-3 shadow-lg shadow-slate-200/50 dark:shadow-black/20">
        {/* Account Type Filters (Left) */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
          {filterTypes.map((type) => {
            const isActive = selectedType === type.value;
            const count = getCount(type.value);
            return (
              <button
                key={type.label}
                onClick={() => setSelectedType(type.value)}
                className={`group flex items-center gap-2 px-3.5 py-2 rounded-2xl text-xs font-black uppercase tracking-wider transition-all duration-200 cursor-pointer border ${isActive
                  ? `${type.activeClass} border-transparent scale-[1.01]`
                  : `bg-white/80 dark:bg-slate-950/30 text-slate-500 dark:text-slate-400 border-slate-100 dark:border-slate-800 ${type.hoverClass}`
                  }`}
              >
                <span
                  className={`rounded-xl flex items-center justify-center transition-colors ${isActive ? "bg-white/20 text-white" : type.iconBg}`}
                  style={{ width: '28px', height: '28px', minWidth: '28px', maxWidth: '28px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <i className={`${type.icon} text-[11px]`} />
                </span>
                <span>{type.label}</span>
                <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-md ${isActive
                  ? "bg-white/20 text-white dark:bg-slate-900/10 dark:text-slate-950"
                  : "bg-slate-200/60 dark:bg-slate-700/80 text-slate-500 dark:text-slate-400"
                  }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* View Toggle & Sorting (Right) */}
        <div className="flex flex-wrap items-center gap-2 self-end xl:self-auto">
          <Dropdown
            value={sortBy}
            options={[
              { label: "İsim (A-Z)", value: "name-asc" },
              { label: "İsim (Z-A)", value: "name-desc" },
              { label: "Bakiye (En Yüksek)", value: "balance-desc" },
              { label: "Bakiye (En Düşük)", value: "balance-asc" },
              { label: "Hesap Türü", value: "type" }
            ]}
            onChange={(e) => setSortBy(e.value)}
            placeholder="Sırala..."
            className="rounded-2xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950/40 font-bold text-xs h-[40px] flex items-center shadow-sm"
            inputClassName="text-xs font-bold py-2 px-3"
          />

          <div className="flex items-center gap-1 bg-slate-100/80 dark:bg-slate-950/40 p-1 rounded-2xl border border-slate-200/70 dark:border-slate-800 shadow-inner">
            <button
              onClick={() => changeViewMode("grid")}
              className={`px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider transition-all flex items-center gap-1.5 cursor-pointer border-none ${viewMode === "grid"
                ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm"
                : "text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 bg-transparent"
                }`}
            >
              <i className="pi pi-th-large text-[10px]" />
              <span>Kart</span>
            </button>
            <button
              onClick={() => changeViewMode("table")}
              className={`px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider transition-all flex items-center gap-1.5 cursor-pointer border-none ${viewMode === "table"
                ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm"
                : "text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 bg-transparent"
                }`}
            >
              <i className="pi pi-list text-[10px]" />
              <span>Tablo</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="pi pi-spin pi-spinner text-4xl text-slate-300 dark:text-slate-700" />
        </div>
      ) : filteredAccounts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl text-center p-8 shadow-sm">
          <div className="w-12 h-12 bg-slate-50 dark:bg-slate-800 rounded-2xl flex items-center justify-center text-slate-400 dark:text-slate-500 mb-4 border border-slate-100 dark:border-slate-800">
            <i className="pi pi-search text-xl" />
          </div>
          <h3 className="text-sm font-black text-slate-800 dark:text-slate-200 uppercase tracking-wider m-0">Kayıt Bulunamadı</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 font-bold mt-2 mb-0 max-w-xs leading-relaxed">
            Bu hesap türünde tanımlanmış herhangi bir hesabınız bulunmuyor.
          </p>
        </div>
      ) : viewMode === "grid" ? (
        /* Grid View */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredAccounts.map((acc) => {
            const style = getStyle(acc.accountType);

            return (
              <div
                key={acc.id}
                onClick={() => {
                  if (acc.isGrouped) {
                    setVirtualPosDialogVisible(true);
                  } else {
                    navigate(`/finance/accounts/detail/${acc.id}`);
                  }
                }}
                className={`group relative ${style.cardBg} border border-l-4 border-white/80 dark:border-slate-800/80 ${style.leftBorder} hover:border-slate-300 dark:hover:border-slate-700 rounded-3xl p-5 shadow-lg shadow-slate-200/50 dark:shadow-black/20 ${style.glow} hover:-translate-y-1 transition-all duration-300 cursor-pointer overflow-hidden flex flex-col justify-between min-h-[178px]`}
              >
                <div className={`absolute -right-10 -top-10 w-28 h-28 rounded-full ${style.accent} opacity-10 group-hover:opacity-20 transition-opacity`} />
                <div className="absolute inset-x-5 top-0 h-px bg-gradient-to-r from-transparent via-white/90 to-transparent dark:via-white/10" />
                {/* Header Info */}
                <div className="w-full">
                  <div className="flex justify-between items-center mb-3">
                    <div className={`!w-8 !h-8 !rounded-lg !flex !items-center !justify-center ${style.iconColor}`}>
                      <i className={`${style.icon} text-sm`} />
                    </div>
                    <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 !rounded-md ${style.badge}`}>
                      {style.label}
                    </span>
                  </div>

                  <div className="flex flex-col min-w-0">
                    <span className="text-slate-600 dark:text-slate-500 text-[12px] font-black uppercase   truncate">
                      {acc.accountNumber || "HESAP NO YOK"}
                    </span>
                    <h2 className="text-[18px] font-black text-slate-800 dark:text-slate-200 mt-0.5 truncate leading-tight group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                      {acc.name}
                    </h2>
                  </div>
                </div>

                {/* Balance Section */}
                <div className="pt-2 border-t border-slate-500 dark:border-slate-800/60 mt-2 flex flex-col justify-end">
                  {acc.accountType === 3 && acc.cardLimit > 0 ? (
                    <div className="space-y-1.5 w-full">
                      <div className="flex justify-between text-[12px] font-black   tracking-widest text-slate-600">
                        <span>Kullanım Oranı</span>
                        <span>{Math.round(Math.abs(acc.balance ?? 0) / acc.cardLimit * 100)}%</span>
                      </div>
                      <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-1 overflow-hidden">
                        <div
                          className={`${style.progress} h-full rounded-full transition-all duration-500`}
                          style={{ width: `${Math.min(100, Math.round(Math.abs(acc.balance ?? 0) / acc.cardLimit * 100))}%` }}
                        />
                      </div>
                      <div className="flex justify-between items-baseline mt-1 text-[11px]">
                        <span className="font-black text-slate-700 dark:text-slate-300">
                          {acc.balance?.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} {acc.currencyCode}
                        </span>
                        <span className="text-[12px] font-bold text-slate-600">
                          Limit: {acc.cardLimit?.toLocaleString("tr-TR", { maximumFractionDigits: 0 })}
                        </span>
                      </div>
                    </div>
                  ) : acc.accountType === 5 ? (
                    <div className="w-full">
                      <span className="text-slate-600 dark:text-slate-500 text-[12x] font-black mb-0.5 block">
                        Toplam İşlem Tutarı
                      </span>
                      <div className="flex items-baseline justify-between w-full">
                        <span className="text-base font-black text-slate-800 dark:text-slate-200">
                          {(acc.totalTransactionAmount ?? 0).toLocaleString("tr-TR", {
                            minimumFractionDigits: 2,
                          })} <span className="text-[10px] font-bold text-slate-400">{acc.currencyCode}</span>
                        </span>
                        {acc.isGrouped && (
                          <button
                            className="text-[9px] font-black !text-blue-600 dark:text-blue-400 hover:text-blue-700 hover:underline uppercase tracking-wider flex items-center gap-1 cursor-pointer border-none bg-transparent p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              setVirtualPosDialogVisible(true);
                            }}
                          >
                            Detay <i className="pi pi-arrow-right text-[8px]" />
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="w-full">
                      <span className="text-slate-600 dark:text-slate-500 text-[12px] font-black uppercase tracking-widest mb-0.5 block">
                        Mevcut Bakiye
                      </span>
                      <div className="flex items-baseline gap-1">
                        <span className={`text-base font-black ${acc.balance >= 0 ? "text-slate-800 dark:text-slate-200" : "text-red-500 dark:text-red-400"}`}>
                          {acc.balance?.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                        </span>
                        <span className="text-[10px] font-bold text-slate-400">{acc.currencyCode}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Edit Button on Hover */}
                {!acc.isGrouped && (
                  <Button
                    icon="pi pi-pencil"
                    className="absolute top-4 right-4 p-button-rounded p-button-text p-button-secondary opacity-0 group-hover:opacity-100 transition-opacity w-7 h-7 !p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/finance/accounts/edit/${acc.id}`);
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* Table View */
        <div className="bg-white/90 dark:bg-slate-900/85 backdrop-blur border border-white/80 dark:border-slate-800 rounded-3xl overflow-hidden shadow-xl shadow-slate-200/60 dark:shadow-black/20">
          <DataTable
            value={filteredAccounts}
            className="p-datatable-sm"
            rowClassName={() => "hover:bg-indigo-50/40 dark:hover:bg-slate-800/70 cursor-pointer transition-colors border-b border-slate-50 dark:border-slate-800/50"}
            emptyMessage="Hesap bulunamadı."
            onRowClick={(e) => {
              if (e.data.isGrouped) {
                setVirtualPosDialogVisible(true);
              } else {
                navigate(`/finance/accounts/detail/${e.data.id}`);
              }
            }}
          >
            <Column
              field="name"
              header="HESAP ADI"
              sortable
              body={(r) => (
                <div className="flex items-center gap-3 py-1">
                  <div className={`!w-8 !h-8 !rounded-lg !flex !items-center !justify-center ${getStyle(r.accountType).iconColor}`}>
                    <i className={`${getStyle(r.accountType).icon} text-sm`} />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-black text-slate-800 dark:text-slate-200 text-sm leading-tight">{r.name}</span>
                    <span className="text-[9px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-widest mt-0.5">{r.accountNumber || "Hesap No Yok"}</span>
                  </div>
                </div>
              )}
            />
            <Column
              field="accountType"
              header="TÜR"
              sortable
              body={(r) => {
                const style = getStyle(r.accountType);
                return (
                  <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md ${style.badge}`}>
                    {style.label}
                  </span>
                );
              }}
            />
            <Column
              field="balance"
              header="TUTAR / BAKİYE"
              sortable
              headerClassName="text-right"
              body={(r) => {
                if (r.accountType === 5) {
                  return (
                    <div className="text-right flex flex-col items-end">
                      <span className="text-slate-400 text-[8px] font-black uppercase tracking-widest mb-0.5">Toplam İşlem</span>
                      <span className="font-black text-slate-800 dark:text-slate-200 text-sm">
                        {(r.totalTransactionAmount ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} {r.currencyCode}
                      </span>
                    </div>
                  );
                }
                return (
                  <div className="text-right flex flex-col items-end">
                    <span className="text-slate-400 text-[8px] font-black uppercase tracking-widest mb-0.5">Mevcut Bakiye</span>
                    <span className={`font-black text-sm ${r.balance >= 0 ? "text-slate-800 dark:text-slate-200" : "text-red-500"}`}>
                      {r.balance?.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} {r.currencyCode}
                    </span>
                  </div>
                );
              }}
            />
            <Column
              body={(r) => (
                <div className="flex justify-end gap-1.5">
                  {r.isGrouped ? (
                    <Button
                      icon="pi pi-external-link"
                      tooltip="Sanal POS'ları Göster"
                      tooltipOptions={{ position: "top" }}
                      className="p-button-rounded p-button-text p-button-secondary w-7 h-7 !p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        setVirtualPosDialogVisible(true);
                      }}
                    />
                  ) : (
                    <>
                      <Button
                        icon="pi pi-pencil"
                        tooltip="Düzenle"
                        tooltipOptions={{ position: "top" }}
                        className="p-button-rounded p-button-text p-button-secondary w-7 h-7 !p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/finance/accounts/edit/${r.id}`);
                        }}
                      />
                      <Button
                        icon="pi pi-chevron-right"
                        tooltip="Detaylar"
                        tooltipOptions={{ position: "top" }}
                        className="p-button-rounded p-button-text p-button-secondary w-7 h-7 !p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/finance/accounts/detail/${r.id}`);
                        }}
                      />
                    </>
                  )}
                </div>
              )}
            />
          </DataTable>
        </div>
      )}
      {/* Transfer Dialog */}
      <Dialog
        header={
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-950/50 text-blue-600">
              <i className="pi pi-sync text-xl"></i>
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-900 dark:text-slate-100 m-0">
                Hesaplar Arası Bakiye Transferi
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 font-medium m-0">
                Kasa ve banka hesaplarınız arasında bakiye aktarımı yapın
              </p>
            </div>
          </div>
        }
        visible={transferDialogVisible}
        style={{ width: 'min(94vw, 480px)' }}
        onHide={() => setTransferDialogVisible(false)}
        className="p-fluid rounded-3xl overflow-hidden"
        appendTo={mounted ? document.body : "self"}
        footer={(
          <div className="flex gap-2">
            <Button
              label="Vazgeç"
              icon="pi pi-times"
              className="p-button-text p-button-secondary font-bold rounded-xl"
              onClick={() => setTransferDialogVisible(false)}
            />
            <Button
              label="Onayla"
              icon="pi pi-check"
              className="border-none font-black text-white !bg-blue-600 hover:!bg-blue-700 rounded-xl"
              onClick={handleTransfer}
              loading={transferLoading}
              disabled={!transferData.sourceAccountId || !transferData.destinationAccountId || !transferData.amount || transferData.amount <= 0}
            />
          </div>
        )}
      >
        <div className="space-y-4 pt-4">
          <div className="field">
            <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">KAYNAK HESAP</label>
            <Dropdown
              value={transferData.sourceAccountId}
              options={accounts.filter(a => a.accountType !== 5)}
              optionLabel="name"
              optionValue="id"
              onChange={(e) => setTransferData({ ...transferData, sourceAccountId: e.value, destinationAccountId: transferData.destinationAccountId === e.value ? null : transferData.destinationAccountId })}
              placeholder="Çıkış yapılacak hesabı seçin"
              className="rounded-xl border-slate-200 font-bold"
              appendTo="self"
              itemTemplate={accountOptionTemplate}
            />
          </div>

          <div className="field">
            <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">HEDEF HESAP</label>
            <Dropdown
              value={transferData.destinationAccountId}
              options={accounts.filter(a => a.accountType !== 5 && a.id !== transferData.sourceAccountId)}
              optionLabel="name"
              optionValue="id"
              onChange={(e) => setTransferData({ ...transferData, destinationAccountId: e.value })}
              placeholder="Giriş yapılacak hesabı seçin"
              className="rounded-xl border-slate-200 font-bold"
              disabled={!transferData.sourceAccountId}
              appendTo="self"
              itemTemplate={accountOptionTemplate}
            />
          </div>

          <div className="field">
            <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">TUTAR</label>
            <InputNumber
              value={transferData.amount}
              onValueChange={(e) => setTransferData({ ...transferData, amount: e.value })}
              min={0}
              placeholder="0.00"
              className="w-full"
              inputClassName="font-black text-lg py-3 rounded-xl border-slate-200 focus:ring-2 focus:ring-slate-500"
              mode="currency"
              currency={accounts.find(a => a.id === transferData.sourceAccountId)?.currencyCode || "TRY"}
              locale="tr-TR"
              disabled={!transferData.sourceAccountId}
            />
          </div>

          <div className="field">
            <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">TRANSFER TARİHİ</label>
            <Calendar
              value={transferData.transferDate}
              onChange={(e) => setTransferData({ ...transferData, transferDate: e.value })}
              dateFormat="dd.mm.yy"
              showTime
              hourFormat="24"
              showIcon
              appendTo={mounted ? document.body : "self"}
              className="w-full font-bold"
              inputClassName="font-bold py-3 rounded-xl border-slate-200"
            />
          </div>

          <div className="field">
            <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider block mb-1.5 ml-1">AÇIKLAMA</label>
            <InputText
              value={transferData.description}
              onChange={(e) => setTransferData({ ...transferData, description: e.target.value })}
              placeholder="Örn: Bankadan Kasaya Çekim, Virman vb."
              className="rounded-xl border-slate-200 py-3 font-bold"
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        header={
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-950/50 text-purple-600">
              <i className="pi pi-globe text-xl"></i>
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-900 dark:text-slate-100 m-0">
                Sanal POS Hesapları
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 font-medium m-0">
                Tedarikçi bazlı sanal POS hesapları ve bu hesapların toplam tahsilatları
              </p>
            </div>
          </div>
        }
        visible={virtualPosDialogVisible}
        style={{ width: "min(94vw, 46rem)" }}
        modal
        onHide={() => setVirtualPosDialogVisible(false)}
        className="rounded-3xl"
        appendTo={mounted ? document.body : "self"}
      >
        <div className="pt-2">
          <DataTable
            value={accounts.filter(a => a.accountType === 5)}
            className="p-datatable-sm rounded-xl overflow-hidden border border-slate-100 dark:border-slate-800"
            rowClassName={() => "hover:bg-indigo-50/40 dark:hover:bg-slate-800/70 cursor-pointer transition-colors border-b border-slate-50 dark:border-slate-800/50"}
            onRowClick={(e) => {
              setVirtualPosDialogVisible(false);
              navigate(`/finance/accounts/detail/${e.data.id}`);
            }}
          >
            <Column
              header="HESAP ADI"
              body={(r) => (
                <div className="flex items-center gap-3 py-1">
                  <div className="!w-8 !h-8 !rounded-lg !flex !items-center !justify-center !text-purple-600 !bg-purple-50 border border-purple-100 dark:bg-purple-950/40 dark:border-purple-900/30">
                    <i className="pi pi-globe text-sm" />
                  </div>
                  <span className="font-bold text-slate-800 dark:text-slate-200 text-sm">{r.name}</span>
                </div>
              )}
            />
            <Column
              header="TOPLAM TAHSİLAT"
              headerClassName="text-right"
              body={(r) => (
                <span className="font-black text-emerald-600 dark:text-emerald-400 text-sm block text-right">
                  {r.totalTransactionAmount?.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} {r.currencyCode}
                </span>
              )}
            />
            <Column
              body={() => <i className="pi pi-chevron-right text-slate-400 dark:text-slate-600" />}
              className="w-[3rem] text-center"
            />
          </DataTable>
        </div>
      </Dialog>
    </div>
  );
}